import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Avatar, IconButton
} from '@mui/material';
import { Visibility, Build, Inventory2 } from '@mui/icons-material';

const statusMeta = {
  pending_review: { label: 'Pending Review', color: 'warning' },
  priced: { label: 'Priced — ready to order', color: 'info' },
  added_to_order: { label: 'Added to Order', color: 'primary' },
  locked: { label: 'Locked (order confirmed)', color: 'success' },
  declined: { label: 'Declined', color: 'error' }
};

const CustomerCustomizationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customization-requests/mine');
      setRequests(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        <Build sx={{ verticalAlign: 'middle', mr: 1 }} />
        My Customization Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Requests filed with Sam (the AI assistant) for modified or custom-built products. Once priced, add the
        customization to your order from its detail page.
      </Typography>

      {requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Build sx={{ fontSize: 56, color: 'text.disabled' }} />
          <Typography variant="body1" color="text.secondary" mt={1}>
            No customization requests yet. Ask Sam about modifying a product to get started.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Product</TableCell>
                <TableCell>Requested Customization</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell>Filed</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => {
                const image = r.product?.images?.find((img) => img.isPrimary) || r.product?.images?.[0];
                const meta = statusMeta[r.status] || { label: r.status, color: 'default' };
                return (
                  <TableRow key={r._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        {image?.url
                          ? <Avatar src={image.url} variant="rounded" />
                          : <Avatar variant="rounded"><Inventory2 fontSize="small" /></Avatar>}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{r.product?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.product?.osesgCode}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap>{r.description}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
                    <TableCell align="right">
                      {typeof r.customizationPrice === 'number' ? `$${r.customizationPrice.toLocaleString()}` : '—'}
                    </TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/customer/customization-requests/${r._id}`} size="small">
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CustomerCustomizationRequests;
